import { useEffect, useCallback, useRef, useState } from "react";
import { Notification } from "@screencloud/screencloud-ui-components";

import { useAppContext } from "../../hooks/useAppContext";
import {
  ScreenDeviceCommandData,
  useCreateScreenDeviceCommandMutation,
} from "../../types.g";
import { useDeviceRdmState } from "./useDeviceRdmState";

export const RDM_COMMAND_TYPE = {
  CLEAR_CACHE: "device.clear_cache",
  REBOOT: "device.reboot",
  RESTART_PLAYER_APP: "device.restart_player_app",
  SET_MUTE: "device.set_mute",
  STATE_REPORT: "device.state.report",
  SCREENSHOT: "screen.capture",
  OS_CHANNEL: "os.channel",
} as const;

export type RdmCommandType =
  (typeof RDM_COMMAND_TYPE)[keyof typeof RDM_COMMAND_TYPE];

// Hold the loading spinner + defer the result toast briefly after a command
// to allow the deviceState refetch to complete before the result is shown.
export const DEFERRED_COMMAND_RESULT_MS = 1500;

export enum CommandStatus {
  PENDING = "PENDING",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
}

function parseApiSchema(
  raw: unknown,
): Record<string, Record<string, string> | Record<string, never>> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  return raw as Record<string, Record<string, string> | Record<string, never>>;
}

export function useRdmCommand(screenId: string) {
  const context = useAppContext();
  const {
    deviceState,
    lastCommand: wsLastCommand,
    isRdmOnline,
  } = useDeviceRdmState(screenId);
  const [createScreenDeviceCommandMutation] =
    useCreateScreenDeviceCommandMutation();

  const apiSchema = parseApiSchema(deviceState?.apiSchema);

  const isCommandSupported = useCallback(
    (commandType: RdmCommandType): boolean =>
      Object.prototype.hasOwnProperty.call(apiSchema, commandType),
    [apiSchema],
  );

  const [pendingCommand, setPendingCommand] =
    useState<ScreenDeviceCommandData | null>(null);

  // Loading in-flight command type: set synchronously on click so the button
  // spinner appears immediately, before the mutation resolves or WS reports PROCESSING.
  const [sendingCommandType, setSendingCommandType] =
    useState<RdmCommandType | null>(null);
  // commandId whose safety timeout fired; its stuck PROCESSING stops counting,
  // so the disable and spinner both clear together on timeout.
  const [staleCommandId, setStaleCommandId] = useState<string | null>(null);
  const safetyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deferredTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLoading = useCallback(() => {
    setSendingCommandType(null);
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  }, []);

  const cancelDeferred = useCallback(() => {
    if (deferredTimerRef.current) {
      clearTimeout(deferredTimerRef.current);
      deferredTimerRef.current = null;
    }
  }, []);

  const defer = useCallback(
    (fn: () => void) => {
      cancelDeferred();
      deferredTimerRef.current = setTimeout(() => {
        deferredTimerRef.current = null;
        fn();
      }, DEFERRED_COMMAND_RESULT_MS);
    },
    [cancelDeferred],
  );

  useEffect(() => {
    setPendingCommand(null);
    clearLoading();
    cancelDeferred();
  }, [screenId, clearLoading, cancelDeferred]);

  useEffect(
    () => () => {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      if (deferredTimerRef.current) clearTimeout(deferredTimerRef.current);
    },
    [],
  );

  const lastCommand =
    wsLastCommand?.commandId === pendingCommand?.commandId
      ? wsLastCommand
      : (wsLastCommand ?? pendingCommand);

  const lastCommandIdRef = useRef<string | null>(null);
  lastCommandIdRef.current = lastCommand?.commandId ?? null;

  const wsProcessing =
    lastCommand?.status === CommandStatus.PROCESSING &&
    lastCommand.commandId !== staleCommandId;

  // TODO: Add translation mapping from RDM `commandType` (e.g. device.clear_cache) to
  // human-readable labels and include them in Notification messages (sent / completed / failed).

  const lastNotifiedRef = useRef<string | null>(null);

  const notifyCommandResult = useCallback(
    (
      status: string | null,
      commandType: string | null,
      commandId: string | null,
    ) => {
      if (commandType === RDM_COMMAND_TYPE.STATE_REPORT) return;

      const dedupeKey = `${commandId ?? "unknown"}:${status}`;
      if (lastNotifiedRef.current === dedupeKey) return;
      lastNotifiedRef.current = dedupeKey;

      if (status === CommandStatus.COMPLETED) {
        Notification.success({
          message: context.intl.formatMessage({
            id: "screens.notification.rdm.completed",
            defaultMessage: "Command completed successfully",
          }),
        });
      } else {
        Notification.error({
          message: context.intl.formatMessage({
            id: "screens.notification.rdm.failed",
            defaultMessage: "Command failed",
          }),
        });
      }
    },
    [context.intl],
  );

  const sendRdmCommand = useCallback(
    async (commandType: RdmCommandType, payload?: Record<string, unknown>) => {
      // Show the spinner immediately on click.
      setSendingCommandType(commandType);
      // Drop any stale finished command
      setPendingCommand(null);
      // Cancel a pending deferred clear/toast from a previous command if it exists
      cancelDeferred();
      // (Re)start the safety timer
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = setTimeout(() => {
        setSendingCommandType(null);
        setStaleCommandId(lastCommandIdRef.current);
        safetyTimerRef.current = null;
      }, 180_000);

      try {
        const result = await createScreenDeviceCommandMutation({
          variables: {
            input: {
              screenId,
              commandType,
              ...(payload !== undefined ? { payload } : {}),
            },
          },
        });

        const commandResult = result.data?.createScreenDeviceCommand;

        if (commandResult?.success === false) {
          clearLoading();
          Notification.error({
            message:
              commandResult.message ??
              context.intl.formatMessage({
                id: "screens.notification.rdm.failed",
                defaultMessage: "Command failed",
              }),
          });
          return null;
        }

        if (commandResult?.data) {
          setPendingCommand(commandResult.data as ScreenDeviceCommandData);
        } else {
          // No commandId will ever arrive for this send; don't hang the spinner.
          clearLoading();
        }

        if (
          commandResult != null &&
          commandType !== RDM_COMMAND_TYPE.STATE_REPORT
        ) {
          Notification.success({
            message: context.intl.formatMessage({
              id: "screens.notification.rdm.sent",
              defaultMessage: "Command sent",
            }),
          });
        }

        return commandResult?.data?.commandId ?? null;
      } catch (e: unknown) {
        clearLoading();
        const message =
          e instanceof Error
            ? e.message
            : context.intl.formatMessage({
                id: "screens.notification.rdm.failed",
                defaultMessage: "Command failed",
              });
        Notification.error({ message });
        return null;
      }
    },
    [
      cancelDeferred,
      clearLoading,
      context.intl,
      createScreenDeviceCommandMutation,
      screenId,
    ],
  );

  // When THIS send's command finishes (COMPLETED/FAILED), defer clearing the
  // spinner and firing the result toast
  useEffect(() => {
    if (!lastCommand?.status) return;
    if (lastCommand.commandId !== pendingCommand?.commandId) return;

    if (lastCommand.status === CommandStatus.PROCESSING) {
      console.log("[RDM] Command processing", {
        commandId: lastCommand.commandId,
        commandType: lastCommand.commandType,
      });
      return;
    }

    // Only COMPLETED/FAILED carry a result to show. PENDING (the create state)
    // and any other in-progress status keep the spinner and fire no toast —
    // otherwise a command that is still running would show a premature
    // "Command failed".
    if (
      lastCommand.status !== CommandStatus.COMPLETED &&
      lastCommand.status !== CommandStatus.FAILED
    ) {
      return;
    }

    const status = lastCommand.status;
    const commandType = lastCommand.commandType;
    const commandId = lastCommand.commandId;

    defer(() => {
      clearLoading();
      notifyCommandResult(status, commandType, commandId);
    });
  }, [
    lastCommand?.status,
    lastCommand?.commandId,
    lastCommand?.commandType,
    pendingCommand?.commandId,
    defer,
    clearLoading,
    notifyCommandResult,
  ]);

  const isCommandProcessing = useCallback(
    (commandType: RdmCommandType): boolean =>
      sendingCommandType === commandType ||
      (lastCommand?.commandType === commandType && wsProcessing),
    [sendingCommandType, lastCommand?.commandType, wsProcessing],
  );

  const isAnyCommandProcessing = sendingCommandType !== null || wsProcessing;

  return {
    isRdmOnline,
    isCommandSupported,
    lastCommand,
    sendRdmCommand,
    isCommandProcessing,
    isAnyCommandProcessing,
    deviceState,
  };
}
